import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Fault, FaultStatus, FaultUpdate } from '@keetcheck/shared';
import { theme } from '../theme.js';
import { useAuth, useAccess } from '../context/AuthContext.js';
import { useBack } from '../lib/back.js';
import {
  useFaults, useFault, useReportFault, useUpdateFault, useAddFaultNote, useResolveFault, useReopenFault,
  useAssets, useSections, useOrgMembers,
} from '../lib/queries.js';
import { assetTypeIcon } from '../lib/asset-types.js';
import { PageShell } from '../components/PageShell.js';
import { Icon } from '../components/Icon.js';
import { StatusPill } from '../components/StatusPill.js';
import { OffRunList, OffRunControl, RespondingStrip, fmtDay } from '../components/OffRun.js';

const STATUS_CHOICES: { value: FaultStatus; label: string }[] = [
  { value: 'broken', label: 'Broken' },
  { value: 'missing', label: 'Missing' },
  { value: 'attention', label: 'Needs attention' },
];

// ── Register ──────────────────────────────────────────────────

/**
 * The fault register: everything found wrong (on a check or reported
 * since) until it's fixed, plus what's off run. ?asset= narrows to one asset.
 */
export function FaultsPage() {
  const nav = useNavigate();
  const back = useBack('/');
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { me } = useAccess();
  const orgId = user?.orgId ?? '';
  const assetId = params.get('asset') ?? '';
  const fixed = params.get('tab') === 'fixed';
  const [mine, setMine] = useState(false);
  const { data, isLoading } = useFaults(orgId, { fixed, assetId });
  const { data: assets = [] } = useAssets(orgId);

  const faults = (data?.faults ?? []).filter((f) => !mine || f.assignedTo?.id === me?.id);
  const groups = useMemo(() => {
    const m = new Map<string, Fault[]>();
    for (const f of faults) m.set(f.assetName, [...(m.get(f.assetName) ?? []), f]);
    return [...m.entries()];
  }, [faults]);
  const offRun = (data?.offRun ?? []).filter((a) => !assetId || a.assetId === assetId);
  const set = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  return (
    <PageShell>
      <Header title="Faults" onBack={back} right={
        <button onClick={() => nav(`/faults/new${assetId ? `?asset=${assetId}` : ''}`)} style={headerBtn}>
          <Icon name="plus" size={15} /> Report
        </button>
      } />
      <div style={{ padding: '10px 16px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: theme.surface, borderBottom: `1px solid ${theme.border}` }}>
        <Seg options={[['open', 'Open'], ['fixed', 'Fixed lately']]} value={fixed ? 'fixed' : 'open'} onChange={(v) => set('tab', v === 'fixed' ? 'fixed' : null)} />
        <select value={assetId} onChange={(e) => set('asset', e.target.value || null)} aria-label="Asset" style={{ ...input, width: 'auto', flex: 1, minWidth: 120, padding: '7px 10px', fontSize: 13 }}>
          <option value="">All assets</option>
          {assets.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        {!fixed && (
          <button onClick={() => setMine(!mine)} style={{ ...chip, ...(mine ? chipOn : {}) }}>Assigned to me</button>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', background: theme.bg, padding: '12px 16px 32px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {!fixed && <RespondingStrip responding={data?.responding} onlyChanged />}
        {!fixed && <OffRunList assets={offRun} />}
        {isLoading && <div style={muted}>Loading…</div>}
        {!isLoading && !faults.length && (
          <div style={{ ...muted, textAlign: 'center', padding: '24px 0' }}>
            {fixed ? 'Nothing fixed in the last 60 days.' : mine ? 'Nothing assigned to you.' : 'No open faults.'}
          </div>
        )}
        {groups.map(([assetName, list]) => (
          <div key={assetName} style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px 6px' }}>
              <Icon name={assetTypeIcon(list[0]!.assetType)} size={17} style={{ color: theme.textMuted }} />
              <span style={{ flex: 1, fontSize: 14, fontWeight: 800, color: theme.text }}>{assetName}</span>
              <span style={{ fontSize: 12, color: theme.textMuted }}>{list.length}</span>
            </div>
            {list.map((f) => <FaultRow key={f.id} f={f} onClick={() => nav(`/faults/${f.id}`)} />)}
          </div>
        ))}
      </div>
    </PageShell>
  );
}

function FaultRow({ f, onClick }: { f: Fault; onClick: () => void }) {
  const age = daysSince(f.reportedAt);
  const meta = f.resolvedAt
    ? [`Fixed ${fmtDay(f.resolvedAt)}`, f.resolvedBy?.displayName, f.resolutionNote]
    : [
      f.sectionName,
      age === 0 ? 'found today' : `open ${age} day${age > 1 ? 's' : ''}`,
      f.sightings > 1 ? `on ${f.sightings} checks` : null,
      f.assignedTo ? `→ ${f.assignedTo.displayName}` : null,
      f.externalRef ? `Ref ${f.externalRef}` : null,
    ];
  return (
    <button onClick={onClick} style={rowBtn}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14, color: theme.text }}>{f.title}</strong>
          {f.offRun && <span style={tag(theme.status.danger)}>Off run</span>}
          {!f.offRun && f.suggestOffRun && !f.resolvedAt && <span style={tag(theme.status.warn)}>Off run?</span>}
        </span>
        <span style={{ display: 'block', fontSize: 12, color: theme.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {meta.filter(Boolean).join(' · ')}
        </span>
      </span>
      {f.resolvedAt ? <StatusPill status="rectified" size="sm" /> : <StatusPill status={f.status} size="sm" />}
      <Icon name="chevron" size={14} style={{ color: theme.textMuted, flexShrink: 0 }} />
    </button>
  );
}

// ── One fault ─────────────────────────────────────────────────

export function FaultDetailPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const back = useBack('/faults');
  const { user } = useAuth();
  const { isAdmin } = useAccess();
  const orgId = user?.orgId ?? '';
  const { data: f, isLoading } = useFault(orgId, id);
  const { data: members = [] } = useOrgMembers(isAdmin ? orgId : '');
  const update = useUpdateFault(orgId, id);
  const addNote = useAddFaultNote(orgId, id);
  const resolve = useResolveFault(orgId, id);
  const reopen = useReopenFault(orgId, id);
  const [note, setNote] = useState('');
  const [fixing, setFixing] = useState(false);
  const [fixNote, setFixNote] = useState('');
  const [ref, setRef] = useState<string | null>(null);

  if (isLoading || !f) {
    return (
      <PageShell>
        <Header title="Fault" onBack={back} />
        <div style={{ padding: 24, ...muted }}>{isLoading ? 'Loading…' : 'Not found.'}</div>
      </PageShell>
    );
  }

  const open = !f.resolvedAt;
  return (
    <PageShell>
      <Header title="Fault" onBack={back} />
      <div style={{ flex: 1, overflowY: 'auto', background: theme.bg, padding: '12px 16px 32px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ ...card, padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: theme.text }}>{f.title}</div>
              <button onClick={() => nav(`/faults?asset=${f.assetId}`)} style={{ ...linkBtn, marginTop: 2, fontSize: 13 }}>
                {f.assetName}{f.sectionName ? ` › ${f.sectionName}` : ''}
              </button>
            </div>
            {open ? <StatusPill status={f.status} /> : <StatusPill status="rectified" />}
          </div>
          {f.note && <div style={{ fontSize: 14, color: theme.text, marginTop: 10, whiteSpace: 'pre-wrap' }}>{f.note}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', fontSize: 13, marginTop: 12, alignItems: 'center' }}>
            <span style={label}>Reported</span>
            <span>{fmtWhen(f.reportedAt)}{f.reportedBy ? ` · ${f.reportedBy.displayName}` : ''}</span>
            {f.sightings > 0 && <><span style={label}>Checks</span><span>Found on {f.sightings} check{f.sightings > 1 ? 's' : ''}, last {fmtWhen(f.lastSeenAt)}</span></>}
            <span style={label}>Assigned</span>
            {isAdmin && open ? (
              <select value={f.assignedTo?.id ?? ''} onChange={(e) => update.mutate({ assignedTo: e.target.value || null })}
                aria-label="Assigned to" style={{ ...input, padding: '6px 8px', fontSize: 13 }}>
                <option value="">Nobody yet</option>
                {members.filter((m) => m.hasApp).map((m) => <option key={m.userId} value={m.userId}>{m.displayName}</option>)}
              </select>
            ) : <span style={{ color: f.assignedTo ? theme.text : theme.textMuted }}>{f.assignedTo?.displayName ?? 'Nobody yet'}</span>}
            <span style={label}>Ref</span>
            {ref === null ? (
              <span>
                {f.externalRef ?? <span style={{ color: theme.textMuted }}>None</span>}
                {open && <button onClick={() => setRef(f.externalRef ?? '')} style={{ ...linkBtn, marginLeft: 8 }}>Edit</button>}
              </span>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); update.mutate({ externalRef: ref.trim() || null }, { onSuccess: () => setRef(null) }); }}
                style={{ display: 'flex', gap: 6 }}>
                <input autoFocus value={ref} onChange={(e) => setRef(e.target.value)} maxLength={100} placeholder="Work order / job no."
                  style={{ ...input, padding: '6px 8px', fontSize: 13 }} />
                <button type="submit" style={{ ...smallBtn, background: theme.accent, color: theme.accentInk, border: 'none' }}>Save</button>
              </form>
            )}
          </div>
          {update.isError && <div style={errText}>{(update.error as Error).message}</div>}
          {open && f.suggestOffRun && !f.assetOffRun && (
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.status.warn.fg, marginTop: 10 }}>
              {f.reportedBy?.displayName ?? 'The reporter'} thinks {f.assetName} shouldn’t be used{isAdmin ? '' : ' — an admin decides'}.
            </div>
          )}
        </div>

        {(open || f.assetOffRun) && (
          <OffRunControl orgId={orgId} assetId={f.assetId} assetName={f.assetName} offRun={f.assetOffRun}
            faultId={f.id} suggestedReason={`${f.title} ${f.status === 'missing' ? 'missing' : f.status === 'broken' ? 'broken' : 'needs attention'}`}
            isAdmin={isAdmin} />
        )}

        {f.photos.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {f.photos.map((p) => (
              <a key={p.id} href={p.url} target="_blank" rel="noreferrer" style={{ display: 'block', aspectRatio: '1', borderRadius: 10, overflow: 'hidden', background: theme.surface2 }}>
                <img src={p.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </a>
            ))}
          </div>
        )}

        {open ? (
          <div style={{ ...card, padding: 12 }}>
            {!fixing ? (
              <button onClick={() => setFixing(true)} style={{ ...bigBtn, background: theme.status.ok.dot }}>
                <Icon name="check" size={16} /> Mark as fixed
              </button>
            ) : (
              <form onSubmit={(e) => {
                e.preventDefault();
                if (fixNote.trim()) resolve.mutate(fixNote.trim(), { onSuccess: () => setFixing(false) });
              }} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>What was done?</div>
                <textarea autoFocus required rows={2} value={fixNote} onChange={(e) => setFixNote(e.target.value)}
                  placeholder="e.g. Replaced with spare from store" style={{ ...input, resize: 'none' }} />
                {resolve.isError && <div style={errText}>{(resolve.error as Error).message}</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => setFixing(false)} style={{ ...bigBtn, flex: 1, background: theme.surface, color: theme.textDim, border: `1px solid ${theme.border}` }}>Cancel</button>
                  <button type="submit" disabled={!fixNote.trim() || resolve.isPending} style={{ ...bigBtn, flex: 2, background: theme.status.ok.dot }}>
                    {resolve.isPending ? 'Saving…' : 'Mark fixed'}
                  </button>
                </div>
              </form>
            )}
            {resolve.data?.stillOffRun && <div style={{ fontSize: 12, color: theme.status.warn.fg, marginTop: 6, fontWeight: 600 }}>{f.assetName} is still off run until an admin puts it back on.</div>}
          </div>
        ) : (
          <div style={{ ...card, padding: 12, background: theme.status.ok.bg }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.status.ok.fg, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="check" size={16} /> Fixed {fmtWhen(f.resolvedAt!)}
            </div>
            <div style={{ fontSize: 13, color: theme.text, marginTop: 4 }}>{f.resolutionNote}</div>
            {f.resolvedBy && <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>{f.resolvedBy.displayName}</div>}
            {isAdmin && (
              <button onClick={() => reopen.mutate()} disabled={reopen.isPending} style={{ ...linkBtn, marginTop: 8 }}>
                {reopen.isPending ? 'Reopening…' : 'Reopen (marked fixed by mistake)'}
              </button>
            )}
            {reopen.isError && <div style={errText}>{(reopen.error as Error).message}</div>}
          </div>
        )}

        <div style={card}>
          <div style={{ ...sectionTitle, padding: '10px 14px 4px' }}>History</div>
          {f.updates.map((u) => <UpdateRow key={u.id} u={u} />)}
          <form onSubmit={(e) => { e.preventDefault(); if (note.trim()) addNote.mutate(note.trim(), { onSuccess: () => setNote('') }); }}
            style={{ display: 'flex', gap: 8, padding: '10px 14px', borderTop: `1px solid ${theme.border}` }}>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} placeholder="Add a note — parts ordered, booked in…"
              style={{ ...input, flex: 1 }} />
            <button type="submit" disabled={!note.trim() || addNote.isPending} aria-label="Add note"
              style={{ ...smallBtn, background: theme.accent, color: theme.accentInk, border: 'none' }}>
              <Icon name="send" size={15} />
            </button>
          </form>
        </div>
      </div>
    </PageShell>
  );
}

const UPDATE_LABEL: Record<FaultUpdate['kind'], string> = {
  reported: 'Reported', seen: 'Still there on a check', note: 'Note', assigned: 'Assigned',
  fixed: 'Marked fixed', found_ok: 'Found OK on a check', reopened: 'Reopened', off_run: 'Taken off run', on_run: 'Back on run',
};

function UpdateRow({ u }: { u: FaultUpdate }) {
  const nav = useNavigate();
  const text = u.kind === 'assigned' ? (u.note ? `Assigned to ${u.note}` : 'Unassigned') : UPDATE_LABEL[u.kind];
  return (
    <div style={{ padding: '8px 14px', borderTop: `1px solid ${theme.border}`, fontSize: 13 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong style={{ color: theme.text }}>{text}</strong>
        <span style={{ fontSize: 12, color: theme.textMuted }}>{u.by?.displayName ?? 'MusterTap'} · {fmtWhen(u.createdAt)}</span>
        {u.itemId && u.runId && (
          <button onClick={() => nav(`/checks/${u.runId}/items/${u.itemId}`)} style={{ ...linkBtn, fontSize: 12 }}>View check</button>
        )}
      </div>
      {u.note && u.kind !== 'assigned' && <div style={{ color: theme.textDim, marginTop: 2, whiteSpace: 'pre-wrap' }}>{u.note}</div>}
    </div>
  );
}

// ── Report one ────────────────────────────────────────────────

/** Report a fault between checks. ?asset= preselects the asset. */
export function FaultNewPage() {
  const nav = useNavigate();
  const back = useBack('/faults');
  const [params] = useSearchParams();
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const { data: assets = [] } = useAssets(orgId);
  const [assetId, setAssetId] = useState(params.get('asset') ?? '');
  const { data: sections = [] } = useSections(orgId, assetId);
  const { data: existing } = useFaults(orgId, { assetId });
  const [equipmentId, setEquipmentId] = useState('');
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState<FaultStatus>('broken');
  const [note, setNote] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [suggestOffRun, setSuggestOffRun] = useState(false);
  const report = useReportFault(orgId);

  // Equipment laid out on the asset (not tasks or readings), by section.
  const items = sections.flatMap((s) => (s.equipment ?? [])
    .filter((e) => (e as unknown as { kind?: string }).kind !== 'reading')
    .map((e) => ({ id: e.equipmentId, name: (e as unknown as { equipmentName: string }).equipmentName, section: s.name })));
  const openFor = new Map((existing?.faults ?? []).filter((f) => f.equipmentId).map((f) => [f.equipmentId!, f.id]));
  const already = equipmentId === '_asset' ? undefined : openFor.get(equipmentId);
  const asset = assets.find((a) => a.id === assetId);
  const valid = !!assetId && !!equipmentId && (equipmentId !== '_asset' || !!title.trim()) && !!note.trim() && !already;

  return (
    <PageShell noNav>
      <Header title="Report a fault" onBack={back} />
      <form onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        report.mutate({
          assetId, status, note: note.trim(), externalRef: externalRef.trim() || null, suggestOffRun,
          ...(equipmentId === '_asset' ? { title: title.trim() } : { equipmentId }),
        }, { onSuccess: (f) => nav(`/faults/${f.id}`, { replace: true }) });
      }} style={{ flex: 1, overflowY: 'auto', background: theme.bg, padding: '14px 16px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Asset">
          <select value={assetId} onChange={(e) => { setAssetId(e.target.value); setEquipmentId(''); }} required style={input}>
            <option value="">Pick one…</option>
            {assets.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>
        {assetId && (
          <Field label="What’s wrong with">
            <select value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)} required style={input}>
              <option value="">Pick…</option>
              <option value="_asset">The {asset?.assetType === 'shed' ? 'building' : 'vehicle'} itself (tyre, lights, engine…)</option>
              {sections.filter((s) => s.equipment?.length).map((s) => (
                <optgroup key={s.id} label={s.name}>
                  {items.filter((i) => i.section === s.name).map((i) => (
                    <option key={`${s.id}-${i.id}`} value={i.id}>{i.name}{openFor.has(i.id) ? ' (already reported)' : ''}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>
        )}
        {already && (
          <div style={{ fontSize: 13, color: theme.status.warn.fg, fontWeight: 600 }}>
            That’s already in the register.{' '}
            <button type="button" onClick={() => nav(`/faults/${already}`)} style={{ ...linkBtn, fontSize: 13 }}>Open it to add a note</button>
          </div>
        )}
        {equipmentId === '_asset' && (
          <Field label="In a few words">
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} required placeholder="e.g. Rear left tyre" style={input} />
          </Field>
        )}
        <Field label="Status">
          <div style={{ display: 'flex', gap: 6 }}>
            {STATUS_CHOICES.map((c) => (
              <button key={c.value} type="button" onClick={() => setStatus(c.value)} style={{ ...chip, flex: 1, padding: '9px 4px', ...(status === c.value ? chipOn : {}) }}>
                {c.label}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Details">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000} required
            placeholder="What you found, and anything done about it" style={{ ...input, resize: 'vertical' }} />
        </Field>
        <Field label="Reference (optional)">
          <input value={externalRef} onChange={(e) => setExternalRef(e.target.value)} maxLength={100} placeholder="Work order / job no." style={input} />
        </Field>
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, color: theme.text, cursor: 'pointer' }}>
          <input type="checkbox" checked={suggestOffRun} onChange={(e) => setSuggestOffRun(e.target.checked)} style={{ marginTop: 3 }} />
          <span>
            It shouldn’t be used
            <span style={{ display: 'block', fontSize: 12, color: theme.textMuted }}>Admins are told straight away and decide whether to take it off run.</span>
          </span>
        </label>
        {report.isError && <div style={errText}>{(report.error as Error).message}</div>}
        <button type="submit" disabled={!valid || report.isPending} style={{ ...bigBtn, background: theme.accent, color: theme.accentInk, opacity: valid ? 1 : 0.5 }}>
          {report.isPending ? 'Sending…' : 'Report fault'}
        </button>
        <div style={{ fontSize: 12, color: theme.textMuted }}>Admins are notified, and the next check of {asset?.name ?? 'the asset'} will show it.</div>
      </form>
    </PageShell>
  );
}

// ── Bits ──────────────────────────────────────────────────────

function Header({ title, onBack, right }: { title: string; onBack: () => void; right?: React.ReactNode }) {
  return (
    <div style={{ padding: '12px 16px', background: theme.surface, borderBottom: `1px solid ${theme.border}`, position: 'sticky', top: 0, zIndex: 5, display: 'flex', alignItems: 'center', gap: 10 }}>
      <button onClick={onBack} aria-label="Back" style={iconBtn}><Icon name="back" size={18} /></button>
      <div style={{ fontSize: 20, fontWeight: 800, color: theme.text, flex: 1 }}>{title}</div>
      {right}
    </div>
  );
}

function Field({ label: text, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={sectionTitle}>{text}</span>
      {children}
    </div>
  );
}

function Seg({ options, value, onChange }: { options: [string, string][]; value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', background: theme.surface2, border: `1px solid ${theme.border}`, borderRadius: 10, padding: 2 }}>
      {options.map(([v, text]) => (
        <button key={v} onClick={() => onChange(v)} style={{
          padding: '6px 12px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font,
          background: v === value ? theme.surface : 'transparent', color: v === value ? theme.text : theme.textMuted,
          boxShadow: v === value ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
        }}>{text}</button>
      ))}
    </div>
  );
}

const daysSince = (iso: string) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400_000));
const fmtWhen = (iso: string) => new Date(iso).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

const tag = (t: { bg: string; fg: string }): React.CSSProperties => ({
  fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: t.bg, color: t.fg,
});
const muted: React.CSSProperties = { fontSize: 13, color: theme.textMuted };
const label: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: theme.textMuted };
const errText: React.CSSProperties = { fontSize: 12, color: theme.status.danger.fg, marginTop: 6 };
const sectionTitle: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: theme.textMuted };
const card: React.CSSProperties = { borderRadius: 12, background: theme.surface, border: `1px solid ${theme.border}`, flexShrink: 0, overflow: 'hidden' };
const rowBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'none', border: 'none',
  borderTop: `1px solid ${theme.border}`, textAlign: 'left', cursor: 'pointer', fontFamily: theme.font,
};
const input: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14, borderRadius: 10,
  border: `1px solid ${theme.border}`, background: theme.surface, color: theme.text, fontFamily: theme.font,
};
const chip: React.CSSProperties = {
  padding: '7px 12px', borderRadius: 999, border: `1px solid ${theme.border}`, background: theme.surface,
  color: theme.textDim, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font,
};
const chipOn: React.CSSProperties = { background: theme.accent, color: theme.accentInk, borderColor: theme.accent };
const bigBtn: React.CSSProperties = {
  width: '100%', padding: '12px 0', borderRadius: 10, border: 'none', color: '#fff', fontSize: 15, fontWeight: 700,
  cursor: 'pointer', fontFamily: theme.font, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
};
const smallBtn: React.CSSProperties = {
  padding: '6px 12px', borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface,
  color: theme.text, fontFamily: theme.font, fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
  display: 'flex', alignItems: 'center', gap: 4,
};
const headerBtn: React.CSSProperties = { ...smallBtn, background: theme.accent, color: theme.accentInk, border: 'none', padding: '8px 12px' };
const linkBtn: React.CSSProperties = {
  padding: 0, background: 'none', border: 'none', cursor: 'pointer', color: theme.accent,
  fontSize: 12, fontWeight: 600, fontFamily: theme.font, textAlign: 'left',
};
const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`, background: theme.surface2, color: theme.text,
  borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
};
